import React, { useState } from 'react';
import { formatDateLong } from '../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  Badge
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DashboardIcon from '@mui/icons-material/Dashboard';
import modernTheme from '../theme/modernTheme';

// Mockup data
const organizations = [
  { 
    id: 1, 
    name: 'ABC School', 
    suburb: 'Melbourne',
    type: 'School',
    status: 'Active',
    isSchool: true,
    hasPending: true
  },
  { 
    id: 2, 
    name: 'XYZ Corporation', 
    suburb: 'Sydney',
    type: 'Business',
    status: 'Active',
    isSchool: false,
    hasPending: false
  },
  { 
    id: 3, 
    name: 'Local News Media', 
    suburb: 'Brisbane',
    type: 'Business',
    status: 'Active',
    isSchool: false,
    hasPending: true
  },
  { 
    id: 4, 
    name: 'Eastside Primary', 
    suburb: 'Perth',
    type: 'School',
    status: 'Active',
    isSchool: true,
    hasPending: false
  },
  { 
    id: 5, 
    name: 'Western High School', 
    suburb: 'Adelaide',
    type: 'School',
    status: 'Inactive',
    isSchool: true,
    hasPending: false
  }
];

const mockOrders = [
  {
    id: 101,
    title: "Back to School Campaign",
    value: 1250.00,
    status: "Approved",
    salesPerson: "Jane Smith",
    createdAt: "2023-07-15"
  },
  {
    id: 102,
    title: "Summer Promotion",
    value: 3275.00,
    status: "In Design",
    salesPerson: "Mike Johnson",
    createdAt: "2023-08-05"
  },
  {
    id: 103,
    title: "Holiday Special",
    value: 5400.00,
    status: "Waiting for Artwork",
    salesPerson: "Jane Smith",
    createdAt: "2023-09-20"
  }
];

// Mockup of Organisations page
function OrganizationsMockup() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(organizations[0]);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [orderTypeTab, setOrderTypeTab] = useState(0);

  // Get icon based on organization type
  const getOrgTypeIcon = (type) => {
    return type === 'School' ? (
      <Avatar sx={{ bgcolor: modernTheme.palette.info.light, width: 32, height: 32 }}>
        <PersonIcon sx={{ fontSize: 18 }} />
      </Avatar>
    ) : (
      <Avatar sx={{ bgcolor: modernTheme.palette.secondary.light, width: 32, height: 32 }}>
        <BusinessIcon sx={{ fontSize: 18 }} />
      </Avatar>
    );
  };

  const getStatusChip = (status) => {
    return (
      <Chip 
        label={status} 
        color={status === 'Active' ? 'success' : 'default'}
        size="small"
        sx={{ borderRadius: '4px', height: '24px' }}
      />
    );
  };

  return (
    <ThemeProvider theme={modernTheme}>
      <Box sx={{ p: { xs: 1.5, sm: 2, lg: 3 }, width: '100%' }}>
        {/* Header section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          borderBottom: `1px solid ${modernTheme.palette.divider}`,
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ mr: 1.5, color: modernTheme.palette.primary.main }} />
            <Typography variant="h4" fontWeight="600" color="primary">
              Organisations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              sx={{ borderRadius: '8px' }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              sx={{ borderRadius: '8px' }}
            >
              Add Organisation
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Left Panel - Organisations List */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2, pb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5 }}>
                    All Organisations
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small">
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small">
                            <SortIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2 }
                    }}
                    sx={{ mb: 2 }}
                  />
                </Box>
                
                <Divider />
                
                <Box sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
                  {organizations.map((org) => (
                    <Box 
                      key={org.id}
                      onClick={() => setSelectedOrganization(org)}
                      sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backgroundColor: selectedOrganization?.id === org.id ? 
                          `${modernTheme.palette.primary.main}10` : 'transparent',
                        borderLeft: selectedOrganization?.id === org.id ? 
                          `3px solid ${modernTheme.palette.primary.main}` : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: selectedOrganization?.id === org.id ? 
                            `${modernTheme.palette.primary.main}10` : 
                            `${modernTheme.palette.grey[100]}80`,
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${modernTheme.palette.divider}`
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getOrgTypeIcon(org.type)}
                        <Box sx={{ ml: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" fontWeight="500">
                              {org.name}
                            </Typography>
                            {org.hasPending && (
                              <Badge 
                                color="error" 
                                variant="dot" 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <LocationOnIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: '0.875rem' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                              {org.suburb}
                            </Typography>
                            <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>•</Box>
                            {getStatusChip(org.status)}
                          </Box>
                        </Box>
                      </Box>
                      <IconButton size="small">
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Organisation Details */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                {selectedOrganization ? (
                  <>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      p: 3, 
                      borderBottom: `1px solid ${modernTheme.palette.divider}`
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: selectedOrganization.isSchool ? 
                              modernTheme.palette.info.main : 
                              modernTheme.palette.secondary.main,
                            width: 48,
                            height: 48
                          }}
                        >
                          {selectedOrganization.isSchool ? <PersonIcon /> : <BusinessIcon />}
                        </Avatar>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="h5" fontWeight="600">
                            {selectedOrganization.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Chip 
                              label={selectedOrganization.type} 
                              size="small" 
                              color={selectedOrganization.isSchool ? "info" : "secondary"}
                              sx={{ 
                                height: '22px', 
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                borderRadius: 1
                              }} 
                            />
                            <Box component="span" sx={{ mx: 1, color: 'text.disabled' }}>•</Box>
                            <Typography variant="body2" color="text.secondary">
                              ID: {selectedOrganization.id}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          sx={{ mr: 1, borderRadius: '8px' }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          sx={{ borderRadius: '8px' }}
                        >
                          New Order
                        </Button>
                      </Box>
                    </Box>
                    
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs 
                        value={detailsTabValue} 
                        onChange={(e, newValue) => setDetailsTabValue(newValue)}
                        sx={{ 
                          px: 3,
                          '& .MuiTab-root': {
                            minWidth: 'auto',
                            px: 2,
                            py: 1.5,
                            mx: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            '&.Mui-selected': {
                              color: 'primary.main',
                              fontWeight: 600
                            }
                          },
                          '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0'
                          }
                        }}
                      >
                        <Tab label="Overview" />
                        <Tab label="Orders" />
                        <Tab label="Contact" />
                        <Tab label="History" />
                      </Tabs>
                    </Box>

                    {/* Orders Tab Content */}
                    {detailsTabValue === 1 && (
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            mb: 2 
                          }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Recent Orders
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TextField
                                placeholder="Search orders..."
                                size="small"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  sx: { borderRadius: 2 }
                                }}
                                sx={{ width: 250, mr: 2 }}
                              />
                              <Button 
                                variant="outlined" 
                                size="small"
                                startIcon={<FilterListIcon />}
                                sx={{ borderRadius: '8px', mr: 1 }}
                              >
                                Filter
                              </Button>
                            </Box>
                          </Box>
                          
                          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${modernTheme.palette.divider}` }}>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Value</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Sales Person</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {mockOrders.map((order) => (
                                  <TableRow key={order.id} hover>
                                    <TableCell>
                                      {order.id}
                                    </TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        fontWeight={600} 
                                        color="success.dark"
                                      >
                                        ${order.value.toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        label={order.status} 
                                        color={
                                          order.status === 'Approved' ? 'success' :
                                          order.status === 'In Design' ? 'warning' :
                                          'primary'
                                        }
                                        size="small"
                                        sx={{ 
                                          height: '24px', 
                                          fontWeight: 500,
                                          borderRadius: '4px'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{order.salesPerson}</TableCell>
                                    <TableCell>{formatDateLong(order.createdAt)}</TableCell>
                                    <TableCell align="right">
                                      <Tooltip title="Edit">
                                        <IconButton size="small">
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button 
                            variant="outlined" 
                            color="primary"
                          >
                            View All Orders
                          </Button>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Contact Tab Preview */}
                    {detailsTabValue === 2 && (
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <PersonIcon color="primary" sx={{ mr: 1 }} />
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    Primary Contact
                                  </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                      Name
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                      John Smith
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                      <EmailIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1rem' }} />
                                      Email
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                      john.smith@example.com
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                      <PhoneIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1rem' }} />
                                      Phone
                                    </Typography>
                                    <Typography variant="body1" fontWeight={500}>
                                      (02) 9876 5432
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    Address
                                  </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="body1" fontWeight={500} paragraph>
                                  123 Main Street
                                </Typography>
                                <Typography variant="body1">
                                  {selectedOrganization.suburb}, NSW 2000
                                </Typography>
                                <Typography variant="body1">
                                  Australia
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    
                    {/* Overview Tab - Just a Preview */}
                    {detailsTabValue === 0 && (
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <Card sx={{ 
                              bgcolor: 'primary.light', 
                              color: 'white',
                              backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)'
                            }}>
                              <CardContent>
                                <Typography variant="overline" fontWeight={600}>
                                  Total Orders
                                </Typography>
                                <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
                                  23
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    3 pending approvals
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Card sx={{ 
                              bgcolor: 'success.light', 
                              color: 'white',
                              backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)'
                            }}>
                              <CardContent>
                                <Typography variant="overline" fontWeight={600}>
                                  Total Revenue
                                </Typography>
                                <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
                                  $24,500
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <AttachMoneyIcon fontSize="small" />
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    $3,275 pending
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Card sx={{ 
                              bgcolor: 'secondary.light', 
                              color: 'white',
                              backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)'
                            }}>
                              <CardContent>
                                <Typography variant="overline" fontWeight={600}>
                                  Active Schools
                                </Typography>
                                <Typography variant="h3" fontWeight={700} sx={{ mt: 1, mb: 2 }}>
                                  17
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CheckCircleOutlineIcon fontSize="small" />
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    2 new this month
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={8}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                  Recent Activity
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box>
                                  {/* This would be a timeline component in the real implementation */}
                                  <Box sx={{ display: 'flex', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
                                      <CheckCircleOutlineIcon fontSize="small" />
                                    </Avatar>
                                    <Box sx={{ ml: 2 }}>
                                      <Typography variant="body1" fontWeight={500}>
                                        Order #102 status changed to "In Design"
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        2 days ago by Mike Johnson
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', mb: 2 }}>
                                    <Avatar sx={{ bgcolor: 'success.light', width: 36, height: 36 }}>
                                      <AttachMoneyIcon fontSize="small" />
                                    </Avatar>
                                    <Box sx={{ ml: 2 }}>
                                      <Typography variant="body1" fontWeight={500}>
                                        New order #103 created ($5,400.00)
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        5 days ago by Jane Smith
                                      </Typography>
                                    </Box>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex' }}>
                                    <Avatar sx={{ bgcolor: 'warning.light', width: 36, height: 36 }}>
                                      <EditIcon fontSize="small" />
                                    </Avatar>
                                    <Box sx={{ ml: 2 }}>
                                      <Typography variant="body1" fontWeight={500}>
                                        Organisation details updated
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        1 week ago by Admin
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                  Organisation Status
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                                    Active Status:
                                  </Typography>
                                  <Chip 
                                    label="Active" 
                                    color="success" 
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                                    Type:
                                  </Typography>
                                  <Typography variant="body1" fontWeight={500}>
                                    {selectedOrganization.type}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                                    Created Date:
                                  </Typography>
                                  <Typography variant="body1" fontWeight={500}>
                                    Jan 15, 2023
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                                    Last Updated:
                                  </Typography>
                                  <Typography variant="body1" fontWeight={500}>
                                    Sep 20, 2023
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ p: 5, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                      Select an organization to view details
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}

export default OrganizationsMockup; 